import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from 'react';
import { cn } from './util';

export type PanelTone = 'white' | 'yellow' | 'blue' | 'red' | 'green' | 'paper' | 'ink';
export type PanelShadow = 'none' | 'sm' | 'md' | 'lg';

const TONE: Record<PanelTone, string> = {
  white: 'bg-white text-ink',
  yellow: 'bg-yellow text-ink',
  blue: 'bg-team-a-tint text-ink',
  red: 'bg-team-b-tint text-ink',
  green: 'bg-jury-tint text-ink',
  paper: 'bg-paper text-ink halftone',
  ink: 'bg-ink text-white',
};

const SHADOW: Record<PanelShadow, string> = {
  none: '',
  sm: 'shadow-ink-sm',
  md: 'shadow-ink',
  lg: 'shadow-ink-lg',
};

export interface PanelProps extends HTMLAttributes<HTMLElement> {
  tone?: PanelTone;
  shadow?: PanelShadow;
  /** Rotation in degrees (hero cards, stickers). */
  rotate?: number;
  /** Border width in px (3 for regular panels, 4 for hero panels). */
  border?: 2 | 3 | 4;
  /** Padding preset. */
  pad?: 'none' | 'sm' | 'md' | 'lg';
  as?: ElementType;
  /** Optional comic caption box stuck to the top-left corner (e.g. "MASADA"). */
  caption?: ReactNode;
  children?: ReactNode;
}

const PAD = { none: '', sm: 'p-2', md: 'p-3', lg: 'p-5' } as const;
const BORDER = { 2: 'border-2', 3: 'border-3', 4: 'border-4' } as const;

/** A comic panel: flat colour, ink border, hard offset shadow, optional tilt and caption box. */
export function Panel({
  tone = 'white',
  shadow = 'sm',
  rotate,
  border = 3,
  pad = 'md',
  as: Tag = 'div',
  caption,
  className,
  style,
  children,
  ...rest
}: PanelProps) {
  const merged: CSSProperties | undefined = rotate ? { rotate: `${rotate}deg`, ...style } : style;
  return (
    <Tag
      className={cn(
        'relative border-ink',
        BORDER[border],
        TONE[tone],
        SHADOW[shadow],
        PAD[pad],
        caption != null && 'pt-6',
        className,
      )}
      style={merged}
      {...rest}
    >
      {caption != null && (
        <span className="comic absolute -top-[3px] -left-[3px] border-3 border-ink bg-yellow px-2 py-0.5 text-[15px]">
          {caption}
        </span>
      )}
      {children}
    </Tag>
  );
}
